import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { PlanChange } from '@/features/billing/PlanChange';
import type { PlanChange as PlanChangeData } from '@/lib/billing';
import './PlanChangeRoute.css';

/**
 * The plan-change route.
 *
 * In the real flow this is entered from Pricing with the chosen plan, and the figures come
 * from the server — proration is computed there from the unused portion of the current period
 * at the old plan's price, and the carried balances are read from the `paid_balance` columns.
 * Never computed in the browser: it cannot be trusted with an invoice and does not know the
 * true period boundaries.
 *
 * The four scenarios below stand in until that endpoint exists, and the switcher mirrors the
 * one in the design file so all four states stay reviewable. Both go when the API lands.
 */

const SCENARIOS: Record<string, { label: string; change: PlanChangeData }> = {
  upgrade: {
    label: 'Upgrade',
    change: {
      kind: 'upgrade',
      fromTier: 'intelligence',
      toTier: 'pro',
      currency: 'USD',
      recurringPrice: 199,
      billingPeriod: 'monthly',
      nextChargeDate: '09/12/2026',
      money: { unusedCredit: 41, remainderCharge: 119.4, dueToday: 78.4, rollsToNextInvoice: 0 },
      carriedBalances: { askAwareQuestions: 140, awareExplainUses: 320 },
      inTrial: false,
      firstChargeAmount: null,
    },
  },
  downgrade: {
    label: 'Downgrade',
    change: {
      kind: 'downgrade',
      fromTier: 'pro',
      toTier: 'intelligence',
      currency: 'USD',
      recurringPrice: 99,
      billingPeriod: 'monthly',
      nextChargeDate: '09/12/2026',
      // More credit than charge, so nothing is taken today and the rest rolls forward.
      money: { unusedCredit: 96, remainderCharge: 49.5, dueToday: 0, rollsToNextInvoice: 46.5 },
      carriedBalances: { askAwareQuestions: 40, awareExplainUses: 120 },
      inTrial: false,
      firstChargeAmount: null,
    },
  },
  'no-balance': {
    label: 'No balance',
    change: {
      kind: 'upgrade',
      fromTier: 'snapshot',
      toTier: 'intelligence',
      currency: 'USD',
      recurringPrice: 99,
      billingPeriod: 'monthly',
      nextChargeDate: '09/12/2026',
      money: { unusedCredit: 6, remainderCharge: 99, dueToday: 93, rollsToNextInvoice: 0 },
      // Both null — no balance held. NOT zero. The carry-over row must be absent.
      carriedBalances: { askAwareQuestions: null, awareExplainUses: null },
      inTrial: false,
      firstChargeAmount: null,
    },
  },
  trial: {
    label: 'Trial',
    change: {
      kind: 'upgrade',
      fromTier: 'snapshot',
      toTier: 'intelligence',
      currency: 'USD',
      recurringPrice: 99,
      billingPeriod: 'monthly',
      nextChargeDate: '08/28/2026',
      // Nothing has been charged, so there is nothing to pro-rate.
      money: null,
      carriedBalances: { askAwareQuestions: null, awareExplainUses: null },
      inTrial: true,
      firstChargeAmount: 99,
    },
  },
};

export function PlanChangeRoute() {
  const navigate = useNavigate();
  const [scenario, setScenario] = useState<string>('upgrade');
  const active = SCENARIOS[scenario]!;

  return (
    <>
      {/* Review affordance only — mirrors the switcher in the design file. Removed with the
          scenarios above once the server supplies the real figures. */}
      <div className="planChangeDev">
        <span>Preview state (dev only)</span>
        {Object.entries(SCENARIOS).map(([key, value]) => (
          <button
            key={key}
            type="button"
            data-active={scenario === key}
            onClick={() => setScenario(key)}
          >
            {value.label}
          </button>
        ))}
      </div>

      <PlanChange
        change={active.change}
        onConfirm={() => {
          // Advances to the hosted checkout overlay with these figures already agreed.
        }}
        onCancel={() => void navigate({ to: '/account' })}
      />
    </>
  );
}
